// Live knowledge-graph canvas (Cytoscape). Renders the working subgraph of a
// run: clause nodes on a deterministic spiral (document order), defined-term
// nodes orbiting the clause that defines them, cross-reference + similarity
// edges from the doc's knowledge graph as a dim base layer. Pipeline events
// light it up:
//   parse.section / section.loaded → clause node appears (heading label;
//     quality:'fragment' renders as parse debris, not a peer clause)
//   entity.term / graph.hit → term node + DEFINES edge glows
//   plan.section   → clause colored by verdict (keep dim / adapt amber / remove red)
//   redraft.started → clause hollow-cyan (sent to the model, reply pending)
//   edit.proposed  → clause turns blue (redraft returned, not yet assembled)
//   edit.applied   → clause turns green
// Node size is relational: clauses grow with their connectivity (references,
// similarity, terms they define), terms grow with how widely they're used —
// the biggest shapes are the load-bearing parts of the document.
// Deterministic layout (no force physics): stable under live streaming AND
// playback scrubbing — recomputing a frame never jitters.
import { useEffect, useMemo, useRef } from 'react';
import cytoscape from 'cytoscape';

const CAP = { clauses: 260, terms: 90, edges: 420 };

// Archimedean spiral in document order — organic, stable, no overlaps at scale.
const clausePos = (i) => {
  const a = 0.62 * i + 2.4;
  const r = 60 + 30 * Math.sqrt(i * 6);
  return { x: Math.cos(a) * r, y: Math.sin(a) * r };
};
const hashAngle = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return ((h % 360) + 360) * (Math.PI / 180);
};
const sizeFor = (degree, base = 8, cap = 26) => Math.min(cap, base + 2.6 * Math.sqrt(degree || 0));
const trim = (s, n) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const STYLE = [
  {
    selector: 'node',
    style: {
      label: '',
      width: 'data(size)',
      height: 'data(size)',
      'background-color': '#2a3b52',
      'transition-property': 'background-color',
      'transition-duration': '0.4s',
    },
  },
  { selector: 'node.clause', style: { shape: 'ellipse' } },
  { selector: 'node.parsed', style: { 'background-color': '#3d5f8f' } },
  // Parse debris (zip codes, mid-citation continuations) — visibly NOT a
  // peer clause, so a "remove" verdict on it doesn't read as deleted law.
  { selector: 'node.fragment', style: { 'background-color': '#26303f', 'border-width': 1, 'border-color': '#3a4a5f', 'border-style': 'dashed' } },
  { selector: 'node.keep', style: { 'background-color': '#41546e' } },
  // Sent to the model in the current redraft call; reply not back yet.
  { selector: 'node.inflight', style: { 'background-color': '#1d4956', 'border-width': 2, 'border-color': '#38d6ff', 'border-style': 'dashed' } },
  { selector: 'node.adapt', style: { 'background-color': '#e0a414' } },
  { selector: 'node.remove', style: { 'background-color': '#d64545' } },
  { selector: 'node.proposed', style: { 'background-color': '#4c8dff' } },
  { selector: 'node.edited', style: { 'background-color': '#27b06c' } },
  { selector: 'node.term', style: { shape: 'diamond', 'background-color': '#5a4f8f' } },
  { selector: 'node.term.hit', style: { 'background-color': '#9d7bff' } },
  // The section(s) currently inside the model — bound to the task's live
  // activeSpan, never animated speculatively.
  { selector: 'node.working', style: { 'background-color': '#38d6ff', 'border-width': 3, 'border-color': '#1a5d73' } },
  // Persistent labels (data flag) + hover/selection labels (class).
  {
    selector: 'node[?lbl], node.labeled, node:selected',
    style: {
      label: 'data(label)',
      color: '#8fa3bd',
      'font-size': 6,
      'text-wrap': 'ellipsis',
      'text-max-width': 110,
      'text-valign': 'top',
      'text-margin-y': -3,
      'min-zoomed-font-size': 5,
    },
  },
  {
    selector: 'node.adapt[?lbl], node.remove[?lbl], node.proposed[?lbl], node.edited[?lbl], node.working, node.term.hit',
    style: { label: 'data(label)', color: '#d9e6f7', 'font-size': 7, 'font-weight': 600 },
  },
  { selector: 'edge', style: { width: 0.7, 'line-color': '#233246', 'curve-style': 'straight', opacity: 0.8 } },
  { selector: 'edge.defines', style: { 'line-color': '#5a4f8f', width: 1.1 } },
  { selector: 'edge.defines.hit', style: { 'line-color': '#9d7bff', width: 1.7 } },
  { selector: 'edge.ref', style: { 'line-color': '#2f4560', width: 1.2 } },
  { selector: 'edge.related', style: { 'line-style': 'dashed', width: 'data(w)', opacity: 0.4 } },
  // Hover: focus the neighborhood, sink everything else.
  { selector: '.dim', style: { opacity: 0.12 } },
  { selector: 'node.hl', style: { 'border-width': 2, 'border-color': '#c9d6e8' } },
  { selector: 'edge.hl', style: { opacity: 1, 'line-color': '#4c6a94' } },
];

// Pure projection: base graph + visible events → node/edge descriptors.
function buildElements(baseGraph, events) {
  const clauses = new Map(); // id → { classes:Set, heading }
  const order = new Map(); // clause id → spiral index
  const ensureClause = (id, heading) => {
    if (!id) return;
    if (!order.has(id)) order.set(id, order.size);
    if (!clauses.has(id)) clauses.set(id, { classes: new Set(['clause']), heading: '' });
    if (heading && !clauses.get(id).heading) clauses.get(id).heading = heading;
  };
  const terms = new Map(); // term → { definedIn, uses, hit }
  const edges = [];

  // Base layer: the doc's knowledge graph, dim until events touch it.
  if (baseGraph?.terms) {
    const numeric = (id) => parseInt(String(id).replace(/\D+/g, ''), 10) || 0;
    const baseIds = new Set();
    for (const t of baseGraph.terms) baseIds.add(t.definedIn);
    for (const r of baseGraph.refs || []) { baseIds.add(r.from); baseIds.add(r.to); }
    for (const r of baseGraph.related || []) { baseIds.add(r.a); baseIds.add(r.b); }
    for (const id of [...baseIds].filter(Boolean).sort((a, b) => numeric(a) - numeric(b))) ensureClause(id);
    for (const t of baseGraph.terms.slice(0, CAP.terms)) {
      terms.set(t.term, { definedIn: t.definedIn, uses: (t.usedIn || []).length, hit: false });
    }
    for (const r of (baseGraph.refs || []).slice(0, 160)) {
      edges.push({ id: `ref-${r.from}-${r.to}-${r.label}`, source: r.from, target: r.to, classes: 'ref' });
    }
    for (const r of (baseGraph.related || []).slice(0, 80)) {
      edges.push({ id: `rel-${r.a}-${r.b}`, source: r.a, target: r.b, classes: 'related', w: 0.5 + (r.score || 0.5) * 1.6 });
    }
  }

  // Event layer.
  for (const e of events) {
    if (e.type === 'parse.section' || e.type === 'section.loaded') {
      ensureClause(e.id, e.heading);
      clauses.get(e.id)?.classes.add('parsed');
      if (e.quality === 'fragment') clauses.get(e.id)?.classes.add('fragment');
    } else if (e.type === 'redraft.started') {
      ensureClause(e.id, e.heading);
      clauses.get(e.id)?.classes.add('inflight');
    } else if (e.type === 'entity.term' || (e.type === 'graph.hit' && e.term)) {
      if (e.definedIn) ensureClause(e.definedIn);
      const cur = terms.get(e.term) || { definedIn: e.definedIn, uses: e.uses || 0, hit: false };
      cur.uses = Math.max(cur.uses || 0, e.uses || 0);
      if (e.type === 'graph.hit') cur.hit = true;
      terms.set(e.term, cur);
    } else if (e.type === 'plan.section') {
      ensureClause(e.id, e.heading);
      const c = clauses.get(e.id);
      c.classes.delete('keep'); c.classes.delete('adapt'); c.classes.delete('remove');
      c.classes.add(e.action === 'adapt' ? 'adapt' : e.action === 'remove' ? 'remove' : 'keep');
      if (e.rationale && !c.rationale) c.rationale = e.rationale;
    } else if (e.type === 'edit.proposed') {
      ensureClause(e.id, e.heading);
      const c = clauses.get(e.id);
      c.classes.delete('inflight');
      if (!c.classes.has('edited')) c.classes.add('proposed');
    } else if (e.type === 'edit.applied') {
      ensureClause(e.id, e.heading);
      const c = clauses.get(e.id);
      c.classes.delete('inflight');
      c.classes.delete('proposed');
      if (!c.classes.has('remove')) c.classes.add('edited');
    }
  }

  // Relational degree: how many edges + term definitions touch each clause.
  const degree = new Map();
  const bump = (id, n = 1) => degree.set(id, (degree.get(id) || 0) + n);
  for (const e of edges) { bump(e.source); bump(e.target); }
  for (const [, t] of terms) if (t.definedIn) bump(t.definedIn, 1 + Math.min(t.uses || 0, 10) / 2);

  // Label policy: terms always; clauses whenever the canvas can afford it
  // (every clause at derive scale, stateful-only on very large documents).
  const labelAllClauses = clauses.size <= 140;

  const nodes = [];
  for (const [id, c] of [...clauses].slice(0, CAP.clauses)) {
    const stateful = ['adapt', 'remove', 'proposed', 'edited', 'inflight'].some((s) => c.classes.has(s));
    nodes.push({
      data: {
        id: `c:${id}`,
        label: c.heading ? `${id} · ${trim(c.heading, 26)}` : id,
        size: sizeFor(degree.get(id), 8, 26),
        lbl: labelAllClauses || stateful ? 1 : 0,
      },
      position: clausePos(order.get(id)),
      classes: [...c.classes].join(' '),
    });
  }
  let termCount = 0;
  for (const [term, t] of terms) {
    if (termCount >= CAP.terms) break;
    if (!t.definedIn || !clauses.has(t.definedIn)) continue;
    termCount += 1;
    const anchor = clausePos(order.get(t.definedIn));
    const a = hashAngle(term);
    const dist = 30 + sizeFor(t.uses, 7, 22);
    nodes.push({
      data: { id: `t:${term}`, label: `${term}${t.uses ? ` ×${t.uses}` : ''}`, size: sizeFor(t.uses, 7, 22), lbl: 1 },
      position: { x: anchor.x + Math.cos(a) * dist, y: anchor.y + Math.sin(a) * dist },
      classes: t.hit ? 'term hit' : 'term',
    });
    edges.push({ id: `def-${term}`, source: `t:${term}`, target: `c:${t.definedIn}`, classes: t.hit ? 'defines hit' : 'defines' });
  }
  const nodeIds = new Set(nodes.map((n) => n.data.id));
  const norm = (x) => (String(x).startsWith('t:') ? x : `c:${x}`);
  const validEdges = edges
    .map((e) => ({ data: { id: e.id, source: norm(e.source), target: norm(e.target), w: e.w || 1 }, classes: e.classes }))
    .filter((e) => nodeIds.has(e.data.source) && nodeIds.has(e.data.target))
    .slice(0, CAP.edges);
  return { nodes, edges: validEdges };
}

export default function GraphCanvas({ baseGraph, events, workingIds = [], height = 380 }) {
  const holder = useRef(null);
  const cyRef = useRef(null);
  const fitted = useRef(false);
  const { nodes, edges } = useMemo(() => buildElements(baseGraph, events), [baseGraph, events]);

  useEffect(() => {
    const cy = cytoscape({
      container: holder.current,
      style: STYLE,
      layout: { name: 'preset' },
      wheelSensitivity: 0.2,
    });
    cyRef.current = cy;
    // Hover = focus: light the neighborhood, sink the rest.
    cy.on('mouseover', 'node', (e) => {
      const hood = e.target.closedNeighborhood();
      cy.batch(() => {
        cy.elements().addClass('dim');
        hood.removeClass('dim').addClass('hl');
        e.target.addClass('labeled');
        hood.nodes().addClass('labeled');
      });
    });
    cy.on('mouseout', 'node', () => {
      cy.batch(() => cy.elements().removeClass('dim hl labeled'));
    });
    return () => cy.destroy();
  }, []);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const nodeIds = new Set(nodes.map((n) => n.data.id));
    cy.batch(() => {
      // Scrubbing back removes elements that haven't "happened" yet.
      cy.nodes().forEach((el) => { if (!nodeIds.has(el.id())) el.remove(); });
      for (const n of nodes) {
        const ex = cy.getElementById(n.data.id);
        if (ex.length) {
          if (ex.classes().join(' ') !== n.classes) ex.classes(n.classes);
          for (const k of ['label', 'size', 'lbl']) if (ex.data(k) !== n.data[k]) ex.data(k, n.data[k]);
        } else {
          cy.add({ group: 'nodes', ...n });
        }
      }
      for (const e of edges) {
        const ex = cy.getElementById(e.data.id);
        if (ex.length) {
          if (ex.classes().join(' ') !== e.classes) ex.classes(e.classes);
          continue;
        }
        if (nodeIds.has(e.data.source) && nodeIds.has(e.data.target)) cy.add({ group: 'edges', ...e });
      }
    });
    if (!fitted.current && cy.nodes().length > 3) {
      cy.fit(undefined, 24);
      fitted.current = true;
    }
  }, [nodes, edges]);

  // Live "in the model now" highlight — separate from the event projection so
  // it can move between flushes without touching the trace.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      cy.nodes('.working').removeClass('working');
      for (const id of workingIds) cy.getElementById(`c:${id}`).addClass('working');
    });
  }, [workingIds, nodes]);

  return (
    <div className="cockpit-canvas" style={{ height }}>
      <div ref={holder} style={{ position: 'absolute', inset: 0 }} />
      {nodes.length === 0 && (
        <div className="cockpit-canvas-empty">
          The knowledge graph lights up here as the run extracts terms, plans sections, and applies edits.
        </div>
      )}
    </div>
  );
}
